<template>
  <click-outside :do="close" :ignore-selector="clickOutsideIgnoreSelector">
    <div class="relative">
      <div ref="triggerRef" @click="toggle">
        <slot name="reference"></slot>
      </div>
      <Teleport to="#dropdown-teleport-root" :disabled="!appendToBody">
        <transition :name="transitionName" @after-enter="updatePanelPosition">
          <div
            v-if="visible"
            ref="panelRef"
            data-dropdown-module-panel
            :class="[dropdownClass, 'dropdown', top ? 'dropdown__top' : 'dropdown__bottom', { 'dropdown--fixed': appendToBody }]"
            :style="appendToBody ? panelStyle : undefined"
            @click.stop="hideOnClick ? close() : null"
          >
            <slot></slot>
          </div>
        </transition>
      </Teleport>
    </div>
  </click-outside>
</template>

<script>
import ClickOutside from '@/components/shared/ClickOutside'

export default {
  components: { ClickOutside },

  props: {
    transitionName: { type: String, default: 'fade' },
    dropdownClass: { type: String },
    hideOnClick: { type: Boolean, default: false },
    top: { default: false },
    appendToBody: { type: Boolean, default: false }
  },

  data () {
    return {
      visible: false,
      panelStyle: {}
    }
  },

  computed: {
    clickOutsideIgnoreSelector () {
      return this.appendToBody ? '[data-dropdown-module-panel]' : ''
    }
  },

  mounted () {
    this.repositionHandler = () => {
      if (this.visible && this.appendToBody) {
        this.updatePanelPosition()
      }
    }
    window.addEventListener('scroll', this.repositionHandler, true)
    window.addEventListener('resize', this.repositionHandler)
  },

  beforeUnmount () {
    window.removeEventListener('scroll', this.repositionHandler, true)
    window.removeEventListener('resize', this.repositionHandler)
  },

  methods: {
    toggle () {
      this.visible = !this.visible
      if (this.visible && this.appendToBody) {
        this.$nextTick(() => {
          this.updatePanelPosition()
          requestAnimationFrame(() => this.updatePanelPosition())
        })
      } else if (!this.visible) {
        this.panelStyle = {}
      }
    },

    close () {
      this.visible = false
      this.panelStyle = {}
    },

    updatePanelPosition () {
      if (!this.appendToBody) return
      const trigger = this.$refs.triggerRef
      const panel = this.$refs.panelRef
      if (!trigger || !panel) return
      const tr = trigger.getBoundingClientRect()
      const pr = panel.getBoundingClientRect()
      const gap = 20
      const margin = 8
      let topPx
      if (this.top) {
        topPx = tr.top - pr.height - gap
      } else {
        topPx = tr.bottom + gap
      }
      let leftPx = tr.right - pr.width + 12
      leftPx = Math.max(margin, Math.min(leftPx, window.innerWidth - pr.width - margin))
      this.panelStyle = {
        position: 'fixed',
        top: `${topPx}px`,
        left: `${leftPx}px`,
        zIndex: 1
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dropdown {
  position: absolute;
  right: 0;
  z-index: 3000;
  pointer-events: auto;
  overflow: visible;
  @apply bg-white;
  @apply border-1;
  @apply border-grey;
  @apply text-xs;
  @apply shadow-md;

  &.notification {
    @apply bg-grey-lightest;
  }

  &--fixed {
    position: fixed;
    right: auto;
    &.dropdown__bottom {
      top: auto;
    }
    &.dropdown__top {
      bottom: auto;
    }
  }

  &__bottom {
    top: calc(100% + 20px);
  }

  &__bottom::before {
    content: "";
    position: absolute;
    z-index: 1;
    right: 11px;
    left: auto;
    top: -12px;
    width: 22px;
    height: 22px;
    box-sizing: border-box;
    background-color: inherit;
    border-left: 1px solid #c6c6c6;
    border-top: 1px solid #c6c6c6;
    transform: rotate(45deg);
    pointer-events: none;
  }

  &__top {
    bottom: calc(100% + 20px);
  }

  &__top::before {
    content: "";
    position: absolute;
    z-index: 1;
    right: 11px;
    left: auto;
    bottom: -12px;
    width: 22px;
    height: 22px;
    box-sizing: border-box;
    background-color: inherit;
    border-right: 1px solid #c6c6c6;
    border-bottom: 1px solid #c6c6c6;
    transform: rotate(45deg);
    pointer-events: none;
  }
}
</style>
